import { Switch, Route, useLocation } from "wouter";
import { Suspense, useDeferredValue, useEffect, useRef } from "react";
import { trackEvent, trackPageView } from "@/lib/analytics";
import { getPublicRoute, PUBLIC_ROUTES, SITE } from "@shared/public-routes";
import { ARTICLE_PAGE_NAMES, CLIENT_PAGE_ROUTES, type PageRoutes } from "@/lib/page-components";
import PageLoadBoundary from "@/components/PageLoadBoundary";
import RouteStyles from "@/components/RouteStyles";
import PageReady from "@/components/PageReady";

const DEFAULT_META = {
  title: "PinkSpider - 404 Not Found",
  description: "The page you're looking for couldn't be found on PinkSpider.",
};

const PUBLIC_ROUTE_PATHS = Object.keys(
  PUBLIC_ROUTES,
) as Array<keyof typeof PUBLIC_ROUTES>;

const PRODUCT_PAGE_PATHS = new Set([
  "/platform",
  "/platform/integrations",
  "/influencer-review",
  "/affiliate-monitoring",
  "/solutions/marketing-review",
  "/academy",
  "/platform/analytics",
  "/platform/automations",
  "/platform/compliance-intelligence",
]);

function setMeta(name: string, content: string) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setOg(property: string, content: string) {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function removeOg(property: string) {
  document.querySelector(`meta[property="${property}"]`)?.remove();
}

function setCanonical(href: string | null) {
  let tag = document.querySelector('link[rel="canonical"]');
  if (!href) {
    tag?.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

function setRouteJsonLd(schema: object | null) {
  const ROUTE_LD_ID = "route-json-ld";
  let tag = document.getElementById(ROUTE_LD_ID) as HTMLScriptElement | null;
  if (!schema) {
    tag?.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = ROUTE_LD_ID;
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(schema);
}

function PageTracker() {
  const [location] = useLocation();
  const isInitialRender = useRef(true);

  useEffect(() => {
    let active = true;
    const reuseServerMetadata = isInitialRender.current &&
      document.querySelector('meta[name="pinkspider:rendered-path"]')?.getAttribute("content") === location;
    isInitialRender.current = false;
    const route = getPublicRoute(location);
    const url = `${window.location.origin}${location}`;
    const canonical =
      route?.indexation === "index"
        ? `${SITE.url}${location === "/" ? "" : location}`
        : null;
    const meta = route ?? DEFAULT_META;
    const indexable = route?.indexation === "index";
    const resource = route?.resource;
    const socialImage = resource?.heroImage
      ? `${SITE.url}${resource.heroImage}`
      : SITE.ogImage;

    if (!reuseServerMetadata) {
      document.title = resource?.seo.title ?? meta.title;
      setMeta("description", meta.description);
      setMeta("robots", indexable ? "index, follow" : "noindex, nofollow");
      setCanonical(canonical);
      setOg("og:type", resource ? "article" : "website");
      setOg("og:title", meta.title);
      setOg("og:description", meta.description);
      setOg("og:url", canonical ?? url);
      if (socialImage) {
        setOg("og:image", socialImage);
        setOg("og:image:width", "1200");
        setOg("og:image:height", "630");
        setOg("og:image:alt", "PinkSpider - AI Compliance for Creator Marketing");
      } else {
        removeOg("og:image");
        removeOg("og:image:width");
        removeOg("og:image:height");
        removeOg("og:image:alt");
      }
      setOg("og:site_name", "PinkSpider");
      setMeta("twitter:card", socialImage ? "summary_large_image" : "summary");
      setMeta("twitter:title", meta.title);
      setMeta("twitter:description", meta.description);
      if (socialImage) {
        setMeta("twitter:image", socialImage);
      } else {
        document.querySelector('meta[name="twitter:image"]')?.remove();
      }
      setOg("article:section", resource?.category ?? "");
      setOg("article:published_time", resource?.publishedAt ?? "");
      setOg("article:modified_time", resource?.reviewedAt ?? "");
      setOg("article:author", resource?.author ?? "");

      // Schema is already in the first response. Download its generator only
      // for client navigation, and never let a slow import update a newer route.
      setRouteJsonLd(null);
      if (indexable) {
        void import("@shared/page-schema").then(({ PAGE_SCHEMA }) => {
          if (active) setRouteJsonLd(PAGE_SCHEMA[location] ?? null);
        }).catch((error: unknown) => {
          console.error("Unable to load route structured data", error);
        });
      }
    }

    if (!route) {
      trackEvent("page_not_found", "error", location, undefined, {
        not_found_path: location,
        referrer_path: document.referrer,
      });
    } else {
      const pageEvent = resource
        ? "resource_view"
        : PRODUCT_PAGE_PATHS.has(location)
          ? "product_page_view"
          : "marketing_page_view";
      trackEvent(pageEvent, resource ? "resource" : "page", location);
    }
    trackPageView(url, meta.title);
    return () => { active = false; };
  }, [location]);

  return null;
}

function ScrollToTop() {
  const [location] = useLocation();
  const isFirstRender = useRef(true);
  useEffect(() => {
    // Skip the very first run (page load / refresh) so the browser's native
    // scroll-position restoration isn't overridden. Only scroll to top on
    // actual client-side navigations between routes.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location]);
  return null;
}

const DOWNLOAD_EXTENSIONS = /\.(pdf|docx?|xlsx?|csv|zip|pptx?)$/i;

function LinkTracker() {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as Element | null)?.closest("a");
      if (!anchor?.href) return;
      const destination = new URL(anchor.href, window.location.origin);
      const label = anchor.textContent?.trim().replace(/\s+/g, " ").slice(0, 100) || "link";
      const currentPath = window.location.pathname;
      const isFooterLink = Boolean(anchor.closest('[data-testid="footer"]'));

      if (destination.protocol === "mailto:") {
        trackEvent("mailto_click", "contact", destination.pathname, undefined, {
          source_path: currentPath,
        });
      } else if (destination.hostname === "app.pinkspider.ai") {
        trackEvent("login_click", "navigation", label);
      } else if (anchor.dataset.integration) {
        trackEvent("outbound_integration_click", "integration", anchor.dataset.integration);
      } else if (destination.pathname === "/demo") {
        const isResourceCta = Boolean(anchor.dataset.resourceCta) || Boolean(getPublicRoute(currentPath)?.resource);
        trackEvent(isResourceCta ? "resource_cta_click" : "demo_cta_click", "cta", label);
      } else if (anchor.dataset.audienceCard) {
        trackEvent("audience_card_click", "homepage", label);
      } else if (anchor.dataset.industryTeaser) {
        trackEvent("industry_teaser_click", "homepage", label);
      } else if (anchor.dataset.homeResourceCard) {
        trackEvent("home_resource_card_click", "homepage", label);
      } else if (DOWNLOAD_EXTENSIONS.test(destination.pathname)) {
        trackEvent("download_click", "resource", label, undefined, {
          file_path: destination.pathname,
          source_path: currentPath,
        });
      } else if (destination.hostname !== window.location.hostname) {
        trackEvent("outbound_link_click", "outbound", label, undefined, {
          destination_host: destination.hostname,
          source_path: currentPath,
        });
      } else if (isFooterLink) {
        trackEvent("footer_link_click", "footer", label, undefined, {
          destination_path: destination.pathname,
        });
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
  return null;
}

function Router({ pages }: { pages: PageRoutes }) {
  const [location] = useLocation();
  // Retain the current page while the next route's module downloads, rather
  // than briefly replacing visible content with a blank suspense fallback.
  const displayedLocation = useDeferredValue(location);
  return (
    <>
      <PageTracker />
      <LinkTracker />
      <ScrollToTop />
      <PageLoadBoundary>
      <Suspense fallback={<p role="status">Loading page…</p>}>
      <div key={displayedLocation} className="page-transition">
        <RouteStyles path={displayedLocation} />
        <Switch location={displayedLocation}>
          {PUBLIC_ROUTE_PATHS.map((path) => (
            <Route
              key={path}
              path={path}
              component={pages[path]}
            />
          ))}
          {Object.keys(ARTICLE_PAGE_NAMES).map((path) => (
            <Route key={path} path={path} component={pages[path as keyof typeof ARTICLE_PAGE_NAMES]} />
          ))}
          <Route component={pages["*"]} />
        </Switch>
        <PageReady />
      </div>
      </Suspense>
      </PageLoadBoundary>
    </>
  );
}

function App({ pages = CLIENT_PAGE_ROUTES }: { pages?: PageRoutes }) {
  return (
    <Router pages={pages} />
  );
}

export default App;
